import React, { useState } from 'react'
import {
  Box,
  Card,
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  Input,
  Textarea,
  Icon
} from '@chakra-ui/react'
import { FiEdit2, FiCheck, FiX } from 'react-icons/fi'

interface AdPreviewCardProps {
  headline: string
  body: string
  rationale?: string
  isEditable?: boolean
  onEdit?: (headline: string, body: string) => void
}

export const AdPreviewCard: React.FC<AdPreviewCardProps> = ({
  headline,
  body,
  rationale,
  isEditable = false,
  onEdit
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editedHeadline, setEditedHeadline] = useState(headline)
  const [editedBody, setEditedBody] = useState(body)

  const handleSave = () => {
    if (onEdit) {
      onEdit(editedHeadline, editedBody)
    }
    setIsEditing(false)
  }

  const handleCancel = () => {
    setEditedHeadline(headline)
    setEditedBody(body)
    setIsEditing(false)
  }

  return (
    <Card.Root
      borderWidth="2px"
      borderColor="brand.200"
      bg="white"
      borderRadius="xl"
      overflow="hidden"
      _hover={{ borderColor: 'brand.300', shadow: 'md' }}
      transition="all 0.2s"
    >
      <Card.Body p={6}>
        <VStack gap={4} align="stretch">
          {/* Header */}
          <HStack justify="space-between" align="start">
            <VStack align="start" gap={1} flex={1}>
              <Heading size="sm" color="gray.900">
                Ad Preview
              </Heading>
              {rationale && (
                <Text fontSize="xs" color="gray.500" fontStyle="italic">
                  {rationale}
                </Text>
              )}
            </VStack>
            {isEditable && !isEditing && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setIsEditing(true)}
              >
                <Icon as={FiEdit2} boxSize={4} />
              </Button>
            )}
          </HStack>

          {/* Ad Content */}
          {isEditing ? (
            <VStack gap={3} align="stretch">
              <Box>
                <Text fontSize="xs" color="gray.600" mb={1} fontWeight="medium">
                  Headline
                </Text>
                <Input
                  value={editedHeadline}
                  onChange={(e) => setEditedHeadline(e.target.value)}
                  size="sm"
                  maxLength={60}
                />
                <Text fontSize="xs" color="gray.400" mt={1}>
                  {editedHeadline.length}/60 characters
                </Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.600" mb={1} fontWeight="medium">
                  Body
                </Text>
                <Textarea
                  value={editedBody}
                  onChange={(e) => setEditedBody(e.target.value)}
                  size="sm"
                  rows={3}
                  maxLength={140}
                />
                <Text fontSize="xs" color="gray.400" mt={1}>
                  {editedBody.length}/140 characters
                </Text>
              </Box>
              <HStack gap={2}>
                <Button
                  size="sm"
                  colorPalette="brand"
                  onClick={handleSave}
                  leftIcon={<Icon as={FiCheck} />}
                >
                  Save
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleCancel}
                  leftIcon={<Icon as={FiX} />}
                >
                  Cancel
                </Button>
              </HStack>
            </VStack>
          ) : (
            <VStack gap={3} align="stretch">
              <Box
                bg="gray.50"
                p={4}
                borderRadius="md"
                borderWidth="1px"
                borderColor="gray.200"
              >
                <Heading size="md" color="gray.900" mb={2}>
                  {headline}
                </Heading>
                <Text fontSize="sm" color="gray.700" lineHeight="tall">
                  {body}
                </Text>
              </Box>
            </VStack>
          )}
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}







